<template>
    <div class="automation-block-editor string-formatter">
        <div class="editor-row customize-editor-minimize">
            <div class="editor-row-title">{{ $t('auto.stringFormatterFormat') }}</div>
            <CustomPicker v-model="clone.mode" :items="formatTypes" idKey="id" labelKey="label" title="Select Format" />
        </div>

        <div class="editor-row customize-editor-minimize data-items">
            <div class="editor-row-title">{{ $t('auto.stringFormatterInputText') }}</div>
            <TiptapEditor
                placeholder="Input Text"
                :show-attributes="true"
                v-model="clone.value"
                :show-emoji="false"
                automation-mode
            />
        </div>
        <StringFormatterDefault v-if="clone.mode === 'string.default'" v-model="clone" />
        <StringFormatterJsonPath v-if="clone.mode === 'string.jsonpath'" v-model="clone" />
        <StringFormatterSplit v-if="clone.mode === 'string.split'" v-model="clone" />
        <StringFormatterReplace v-if="clone.mode === 'string.replace'" v-model="clone" />
        <StringFormatterTruncate v-if="clone.mode === 'string.truncate'" v-model="clone" />
        <StringFormatterTrim v-if="clone.mode === 'string.trim'" v-model="clone" />
        <StringFormatterRegex v-if="clone.mode === 'string.regex'" v-model="clone" />
        <StringFormatterFind v-if="clone.mode === 'string.find'" v-model="clone" />
        <StringFormatterWordCount v-if="clone.mode === 'string.word_count'" v-model="clone" />
        <StringFormatterExtractPhoneNumber v-if="clone.mode === 'string.extract_phone_number'" v-model="clone" />
    </div>
</template>

<script>
import log2 from "../../../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../../../Settings/components/SupsisButton.vue";
import DataItem from "../../webhook/DataItem.vue";
import StringFormatterDefault from "./StringFormatter.Default.vue";
import StringFormatterJsonPath from "./StringFormatter.JsonPath.vue";
import StringFormatterSplit from "./StringFormatter.Split.vue";
import StringFormatterReplace from "./StringFormatter.Replace.vue";
import StringFormatterTruncate from "./StringFormatter.Truncate.vue";
import StringFormatterTrim from "./StringFormatter.Trim.vue";
import StringFormatterRegex from "./StringFormatter.Regex.vue";
import StringFormatterFind from "./StringFormatter.Find.vue";
import StringFormatterWordCount from "./StringFormatter.WordCount.vue";
import StringFormatterExtractPhoneNumber from "./StringFormatter.ExtractPhoneNumber.vue";

const log = log2("StringFormatter");
export default {
    name: "StringFormatter",
    components: { StringFormatterJsonPath, StringFormatterDefault, StringFormatterSplit, DataItem, SupsisButton, CustomPicker, TiptapEditor, StringFormatterReplace, StringFormatterTruncate, StringFormatterTrim, StringFormatterRegex, StringFormatterFind, StringFormatterWordCount, StringFormatterExtractPhoneNumber },
    props: ["value"],
    data() {
        const clone = _.cloneDeep(this.value) || {
            mode: "string.capitalize",
            value: "",
            regex: {
                pattern: "",
                flags: "",
                matchAll: false,
            },
            find: {
                skip: "0",
                pattern: "",
            },
            split: {
                delimiter: " ",
            },
            truncate: {
                maxLength: "0",
                skipChars: "0",
                appendEllipsis: false,
            },
            default: {
                value: "",
            },
        };
        return {
            clone,
            formatTypes: [
                {
                    id: "string.capitalize",
                    label: "Capitalize",
                    description: "Capitalize the first letter of each word.",
                },
                { id: "string.lowercase", label: "Lowercase", description: "Convert all characters to lowercase." },
                { id: "string.uppercase", label: "Uppercase", description: "Convert all characters to uppercase." },
                {
                    id: "string.titlecase",
                    label: "Titlecase",
                    description: "Convert the first letter of each word to uppercase.",
                },
                { id: "string.regex", label: "Regex", description: "Use regular expression to format the text." },
                { id: "string.find", label: "Find", description: "Find a pattern in the text." },
                { id: "string.split", label: "Split", description: "Split the text by a delimiter." },
                { id: "string.replace", label: "Replace", description: "Replace a pattern in the text." },
                { id: "string.truncate", label: "Truncate", description: "Truncate the text to a certain length." },
                { id: "string.default", label: "Default", description: "Use a default value if the text is empty." },
                { id: "string.trim", label: "Trim Whitespace", description: "Remove whitespace from the text." },

                {
                    id: "string.html2markdown",
                    label: "HTML to Markdown",
                    description: "Convert HTML to Markdown.",
                },
                {
                    id: "string.markdown2html",
                    label: "Markdown to HTML",
                    description: "Convert Markdown to HTML.",
                },
                {
                    id: "string.xml2json",
                    label: "XML to JSON",
                    description: "Convert XML to JSON.",
                },
                {
                    id: "string.jsonpath",
                    label: "Read JSON Path",
                    description: "Read a value from JSON using JSONPath.",
                },
                {
                    id: "string.remove_html",
                    label: "Remove HTML Tags",
                    description: "Remove HTML tags from the text.",
                },
                {
                    id: "string.encode_ascii",
                    label: "Encode ASCII",
                    description: "Encode the text to ASCII.",
                },
                {
                    id: "string.extract_phone_number",
                    label: "Extract Phone Number",
                    description: "Extract phone number from the text.",
                },
                {
                    id: "string.extract_email",
                    label: "Extract Email",
                    description: "Extract email from the text.",
                },
                {
                    id: "string.extract_url",
                    label: "Extract URL",
                    description: "Extract URL from the text.",
                },
                {
                    id: "string.extract_number",
                    label: "Extract Number",
                    description: "Extract number from the text.",
                },

                {
                    id: "string.extract_hashtag",
                    label: "Extract Hashtag",
                    description: "Extract hashtag from the text.",
                },
                {
                    id: "string.extract_mention",
                    label: "Extract Mention",
                    description: "Extract mention from the text.",
                },
                {
                    id: "string.word_count",
                    label: "Word Count",
                    description: "Count the number of words in the text.",
                },
                {
                    id: "string.encode_url",
                    label: "Encode URL",
                    description: "Encode the text to URL.",
                },
                {
                    id: "string.decode_url",
                    label: "Decode URL",
                    description: "Decode the text from URL.",
                },
                {
                    id: "array.shuffle",
                    label: "Shuffle",
                    description: "Shuffle the array.",
                },
                {
                    id: "array.sort",
                    label: "Sort",
                    description: "Sort the array.",
                },
                {
                    id: "array.reverse",
                    label: "Reverse",
                    description: "Reverse the array.",
                },
                {
                    id: "array.sample",
                    label: "Sample",
                    description: "Sample a random item from the array.",
                },
                {
                    id: "array.takeFirst",
                    label: "Take First",
                    description: "Take the first item from the array.",
                },
                {
                    id: "array.takeLast",
                    label: "Take Last",
                    description: "Take the last item from the array.",
                },
            ],
        };
    },
    methods: {
        addItem() {
            this.clone.items.push({ key: null, value: null });
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.automation-block-editor.string-formatter {
    padding: 20px;
    display: flex;
    gap: 20px;
    flex-direction: column;

    .editor-row {
        width: 100%;

        &.customize-editor-minimize {
            display: flex;
            flex-direction: column;
            gap: 5px;
        }

        small.description {
            font-size: 10px;
            color: var(--color-text-weak-default);
        }

        .editor-row-title {
            font-weight: bold;
            font-size: 16px;
        }
    }
}
</style>
